import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as authService from '../services/auth.service';
import User from '../types/user.type';
import { useResetAppState } from './useResetAppState';

interface AuthContextType {
  user?: User;
  loading: boolean;
  error?: Error | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  loading: false,
  login: async () => {},
  logout: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<User | undefined>();
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingInitial, setLoadingInitial] = useState<boolean>(true);

  const navigate = useNavigate();
  const location = useLocation();
  const resetAppState = useResetAppState();

  // Reset error state when location changes (but not on login page)
  useEffect(() => {
    if (error && location.pathname !== '/login') {
      setError(null);
    }
  }, [location.pathname, error]);

  // Check for existing session on mount
  useEffect(() => {
    const checkCurrentUser = async (): Promise<void> => {
      try {
        const currentUser = await authService.getCurrentUser();
        setUser(currentUser);
      } catch (err: unknown) {
        console.error('Error checking current user:', err);
        setUser(undefined);
      } finally {
        setLoadingInitial(false);
      }
    };

    checkCurrentUser();
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const result = await authService.login(email, password);

      if (result instanceof Error) {
        setError(result);
      } else if (result.isLoggedIn) {
        setUser(result);
        navigate('/', { replace: true });
      } else {
        setError(new Error('Login failed. Please try again.'));
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err : new Error('An unexpected error occurred');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const logout = useCallback((): void => {
    authService.logout();
    resetAppState();
    setUser(undefined);
    setError(null);
    navigate('/login');
  }, [navigate, resetAppState]);

  // Auto-refresh token every 10 minutes
  useEffect(() => {
    const refreshInterval = 1000 * 60 * 10; // 10 minutes
    const interval = setInterval(async () => {
      try {
        await authService.refreshToken();
      } catch (err: unknown) {
        console.error('Token refresh failed:', err);
        logout();
      }
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [logout]);

  const memoedValue = useMemo(
    () => ({
      user,
      loading,
      error,
      login,
      logout,
    }),
    [user, loading, error, login, logout],
  );

  if (loadingInitial) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div style={{
          color: 'white',
          fontSize: '18px',
          fontWeight: 500
        }}>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={memoedValue}>
      {children}
    </AuthContext.Provider>
  );
}

export default function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
